import React from 'react';

import { SelectChangeEvent } from '@mui/material';
import {
  ChipWrapper,
  CloseRoundedWrapper,
  FormControlWrapper,
  FormHelperText,
  LabelWrapper,
  MenuItemWrapper,
  SelectWrapper,
  SelectValueWrapper,
} from './Select.style';

import { Option, SelectProps, SelectValueType } from './Select.type';

function Select<T extends SelectValueType>({
  dataTestID,
  isMultiSelect = false,
  isRequired,
  label,
  onMultiSelect,
  onSingleSelect,
  options,
  selectedOption,
  selectedOptions,
  error,
  errorMessage,
  startIcon,
  sx,
}: SelectProps<T>): JSX.Element {
  function singleSelectHandler(e: SelectChangeEvent<unknown>) {
    const { value } = e.target;
    const singleSelectedOption = options.find(
      (option) => option.value === value,
    );

    if (singleSelectedOption && onSingleSelect) {
      onSingleSelect(singleSelectedOption);
    }
  }

  function multiSelectHandler(e: SelectChangeEvent<unknown>) {
    const values = (e.target.value as string).split(',');
    const mappedOptions = values
      .map((value) => options.find((option) => option.value === value))
      .filter(Boolean) as Option<T>[];

    if (onMultiSelect) {
      onMultiSelect(mappedOptions);
    }
  }

  function handleDelete(
    selectedValues: string[],
    currentlySelectedValue: string,
  ) {
    const mappedOptions = selectedValues
      .filter((selectedValue) => selectedValue !== currentlySelectedValue)
      .map((value) => options.find((option) => option.value === value))
      .filter(Boolean) as Option<T>[];

    if (onMultiSelect) {
      onMultiSelect(mappedOptions);
    }
  }

  function onValueMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();
  }

  function renderValue() {
    return (
      <SelectValueWrapper>
        {startIcon ?? ''}
        {selectedOption?.label}
      </SelectValueWrapper>
    );
  }

  const selectedValue = isMultiSelect
    ? selectedOptions?.map(({ value }) => value) ?? []
    : selectedOption?.value;

  function renderMultiValues() {
    const selectedValues = selectedValue as string[];
    return selectedValues.map((currentlySelectedValue) => {
      function onDelete() {
        handleDelete(selectedValues, currentlySelectedValue);
      }

      return (
        <ChipWrapper
          data-testid={`chip-${currentlySelectedValue}`}
          deleteIcon={<CloseRoundedWrapper />}
          key={currentlySelectedValue}
          label={
            options.find((option) => option.value === currentlySelectedValue)
              ?.label
          }
          onDelete={onDelete}
          onMouseDown={onValueMouseDown}
        />
      );
    });
  }

  return (
    <FormControlWrapper error={error} sx={sx}>
      <LabelWrapper htmlFor="select" required={isRequired}>
        {label}
      </LabelWrapper>
      <SelectWrapper
        displayEmpty
        data-testid={dataTestID}
        id="select"
        MenuProps={{
          PaperProps: {
            style: {
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
            },
          },
        }}
        multiple={isMultiSelect}
        onChange={isMultiSelect ? multiSelectHandler : singleSelectHandler}
        required={isRequired}
        value={selectedValue}
        renderValue={isMultiSelect ? renderMultiValues : renderValue}
      >
        {options.map((option) => (
          <MenuItemWrapper
            data-testid={`option-${option.value}`}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItemWrapper>
        ))}
      </SelectWrapper>
      {error && errorMessage ? (
        <FormHelperText>{errorMessage}</FormHelperText>
      ) : null}
    </FormControlWrapper>
  );
}

export { Select };
